import { getBackendBaseUrl, isDesktopApp, isDesktopBackendReady, desktopPrefersLocalStorage } from '@/services/config/env';
import { withAccessToken } from '@/lib/avatarCastAuthUrl';

function backendOrigin() {
  if (isDesktopApp() && !isDesktopBackendReady()) return '';
  return getBackendBaseUrl();
}

/** Strip accidental `http://host/file://…` prefixes saved by older desktop builds. */
export function normalizeMalformedLocalMediaUrl(url) {
  const s = String(url || '').trim();
  if (!s) return s;
  const idx = s.indexOf('file://');
  if (idx > 0) return s.slice(idx);
  return s;
}

/** Re-point /api/media URLs at the current desktop backend port (saved clips may embed an old port). */
function rewriteLocalBackendMediaUrl(url) {
  const base = backendOrigin();
  if (!base || !url) return url;
  try {
    const parsed = new URL(String(url));
    const isLocal =
      parsed.hostname === '127.0.0.1'
      || parsed.hostname === 'localhost'
      || parsed.hostname === '[::1]';
    if (isLocal && parsed.pathname.startsWith('/api/')) {
      return `${base}${parsed.pathname}${parsed.search}`;
    }
  } catch {
    /* not a URL */
  }
  return url;
}

/**
 * Pick the best URL for playback from split storage fields (web vs desktop).
 * Prefer explicit s3/local fields when the legacy url is a desktop file:// path.
 */
export function resolveMediaPlaybackUrl({
  url,
  s3Url,
  localUrl,
} = {}) {
  const legacy = normalizeMalformedLocalMediaUrl(String(url || '').trim());
  const s3 = String(s3Url || '').trim();
  const local = normalizeMalformedLocalMediaUrl(String(localUrl || '').trim());
  if (isDesktopApp() && desktopPrefersLocalStorage()) {
    return local || s3 || legacy || '';
  }
  // Web saves go straight to the cloud. The current URL is that value.
  if (!isDesktopApp() && isWebCloudUrl(legacy)) return legacy;
  if (s3 && (s3.startsWith('https://') || s3.startsWith('http://'))) return s3;
  if (legacy && (legacy.startsWith('https://') || legacy.startsWith('http://'))) return legacy;
  if (isDesktopApp() && local) return local;
  return '';
}

function isWebCloudUrl(url) {
  const s = String(url || '').trim();
  if (!s.startsWith('https://') && !s.startsWith('http://')) return false;
  return !isLocalMediaRef(s);
}

const IMAGE_MEDIA_EXT = /\.(png|jpe?g|webp|gif|bmp|svg|avif)$/i;
const VIDEO_MEDIA_EXT = /\.(mp4|webm|mov|m4v|mkv|avi)$/i;

function mediaPathname(url) {
  return String(url || '').trim().split('?')[0];
}

/** True when the URL path is a still (jpg/png/…). Export must not treat these as an MP4. */
export function isImageMediaUrl(url) {
  const path = mediaPathname(url);
  return Boolean(path) && IMAGE_MEDIA_EXT.test(path);
}

/** True when the URL path is a video file. */
export function isVideoMediaUrl(url) {
  const path = mediaPathname(url);
  return Boolean(path) && VIDEO_MEDIA_EXT.test(path);
}

/** Compiled-video URL only — empty for stills the pipelines store on result_url. */
export function playableFinalVideoUrl(url) {
  const raw = String(url || '').trim();
  if (!raw || isImageMediaUrl(raw)) return '';
  return raw;
}

/** Resolve stored or relative URLs for playback in the browser (img/audio/video.src, etc.). */
export function mediaUrlForPlayback(url, options = {}) {
  if (!url && !options.s3Url && !options.localUrl) return url;
  if (!isDesktopApp() && (options.s3Url || options.localUrl)) {
    const resolved = resolveMediaPlaybackUrl({
      url,
      s3Url: options.s3Url,
      localUrl: options.localUrl,
    });
    if (resolved) return mediaUrlForPlayback(resolved);
    if (String(url || '').trim().startsWith('file://')) return '';
  }
  if (!url) return url;
  let s = normalizeMalformedLocalMediaUrl(String(url).trim());
  if (!s) return url;
  if (!isDesktopApp() && s.startsWith('file://')) return '';
  if (s.startsWith('blob:') || s.startsWith('data:') || s.startsWith('file://')) return s;
  if (s.startsWith('http://') || s.startsWith('https://')) {
    return withAccessToken(rewriteLocalBackendMediaUrl(s));
  }
  const base = backendOrigin();
  if (!base) return withAccessToken(s);
  if (s.startsWith('/')) return withAccessToken(`${base}${s}`);
  return withAccessToken(`${base}/${s}`);
}

/** Append a cache-buster so browsers reload media after in-place URL overwrites. */
export function mediaUrlWithCacheBust(url, versionToken) {
  const resolved = mediaUrlForPlayback(url);
  if (!resolved || versionToken == null || versionToken === '') return resolved;
  if (resolved.startsWith('blob:') || resolved.startsWith('data:') || resolved.startsWith('file://')) {
    return resolved;
  }
  const token = encodeURIComponent(String(versionToken));
  const sep = resolved.includes('?') ? '&' : '?';
  return `${resolved}${sep}v=${token}`;
}

/** crossOrigin value safe for canvas/VFX capture (skip blob/file/data URLs). */
export function mediaCrossOriginForCanvas(url) {
  const s = String(url || '').trim();
  if (!s || s.startsWith('blob:') || s.startsWith('file:') || s.startsWith('data:')) return undefined;
  if (s.startsWith('http://') || s.startsWith('https://') || s.startsWith('/')) return 'anonymous';
  return undefined;
}

/**
 * For saved project JSON: drop backend origin prefix so paths stay portable.
 * Returns null for blob previews (caller should omit or reconcile).
 */
export function persistedMediaUrl(url) {
  if (!url || typeof url !== 'string') return url;
  const trimmed = url.trim();
  if (!trimmed) return trimmed;
  if (trimmed.startsWith('blob:')) return null;
  const base = backendOrigin();
  if (!base) return trimmed;
  if (trimmed.startsWith(`${base}/`)) return trimmed.slice(base.length);
  if (trimmed === base) return '';
  return trimmed;
}

/** Stable key for comparing media refs across signed S3 URLs and /api/media/{id}. */
export function mediaUrlIdentityKey(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  const withoutQuery = raw.split('?')[0];
  const mediaMatch = withoutQuery.match(/\/api\/media\/([^/?#]+)/i);
  if (mediaMatch) return `media:${decodeURIComponent(mediaMatch[1])}`;
  if (withoutQuery.includes('amazonaws.com')) {
    try {
      const parsed = new URL(withoutQuery);
      return `s3:${parsed.pathname}`;
    } catch {
      return `s3:${withoutQuery}`;
    }
  }
  return persistedMediaUrl(withoutQuery) || withoutQuery;
}

export function mediaUrlForSegmentField(segment, field = 'image_url') {
  if (!segment) return '';
  const base = field.endsWith('_url') ? field.slice(0, -4) : field;
  const primary = segment[field];
  const s3Url = segment[`${base}_s3_url`];
  const localUrl = segment[`${base}_local_url`];
  // Canonical *_url is the active media version (select-media / upload). Do not let a stale
  // *_s3_url from a previous generation override the newly selected primary URL.
  if (primary) {
    const fromPrimary = mediaUrlForPlayback(primary);
    if (fromPrimary) return fromPrimary;
  }
  return mediaUrlForPlayback(primary, { s3Url, localUrl });
}

function isCloudS3Url(url) {
  const s = String(url || '').trim();
  return s.includes('amazonaws.com') && (s.startsWith('https://') || s.startsWith('http://'));
}

function isLocalMediaRef(url) {
  const s = normalizeMalformedLocalMediaUrl(String(url || '').trim());
  return Boolean(s && (s.startsWith('file://') || s.includes('/api/storage/local/')));
}

/**
 * Cloud value to persist. The current URL wins. image_s3_url / local are used
 * only when the current value is missing or still a local file.
 */
function storagePrimaryFromMediaRef(ref, existingS3, existingLocal) {
  const s3 = String(existingS3 || '').trim();
  const local = normalizeMalformedLocalMediaUrl(String(existingLocal || '').trim());
  const current = normalizeMalformedLocalMediaUrl(String(ref || '').trim());
  if (isWebCloudUrl(current)) {
    if (isCloudS3Url(current)) {
      return { primary: current, s3Url: current, localUrl: null };
    }
    return { primary: current, s3Url: null, localUrl: null };
  }
  if (s3 && isCloudS3Url(s3)) {
    return { primary: s3, s3Url: s3, localUrl: null };
  }
  if (isLocalMediaRef(current) || isLocalMediaRef(local)) {
    return { primary: null, s3Url: s3 || null, localUrl: local || current || null };
  }
  return { primary: null, s3Url: s3 || null, localUrl: local || null };
}

const EDITOR_CLIP_STORAGE_MAP = [
  ['videoUrl', 'video'],
  ['imageUrl', 'image'],
  ['audioUrl', 'audio'],
  ['url', 'audio'],
];

function sanitizeEditorClipForStorage(clip) {
  if (!clip || typeof clip !== 'object') return clip;
  const next = { ...clip };
  for (const [clipKey, base] of EDITOR_CLIP_STORAGE_MAP) {
    if (!(clipKey in next)) continue;
    const raw = next[clipKey];
    const s3Val = next[`${base}_s3_url`];
    const localVal = next[`${base}_local_url`];
    const { primary, s3Url, localUrl } = storagePrimaryFromMediaRef(raw, s3Val, localVal);
    if (primary && isWebCloudUrl(primary)) {
      next[clipKey] = primary;
      if (s3Url) next[`${base}_s3_url`] = s3Url;
      else delete next[`${base}_s3_url`];
      delete next[`${base}_local_url`];
    } else if (isLocalMediaRef(raw) || isLocalMediaRef(localVal)) {
      next[clipKey] = primary || null;
      if (localUrl) next[`${base}_local_url`] = localUrl;
    } else if (localUrl) {
      next[`${base}_local_url`] = localUrl;
    }
  }
  return next;
}

function sanitizeHostOverlayClipForStorage(clip) {
  if (!clip || typeof clip !== 'object') return clip;
  const next = { ...clip };
  const raw = String(next.imageUrl || next.image_url || '').trim();
  const s3 = String(next.image_s3_url || next.imageS3Url || '').trim();
  const ref = raw || s3;
  if (!ref) return next;
  const { primary, s3Url } = storagePrimaryFromMediaRef(raw, s3, null);
  if (primary && isWebCloudUrl(primary)) {
    next.imageUrl = primary;
    if (s3Url) next.image_s3_url = s3Url;
    else delete next.image_s3_url;
  } else if (raw.startsWith('blob:')) {
    next.imageUrl = '';
    delete next.image_s3_url;
  } else {
    const persisted = persistedMediaUrl(raw || s3);
    next.imageUrl = persisted || raw || s3;
    if (s3 || (persisted && persisted.includes('amazonaws.com'))) {
      next.image_s3_url = persistedMediaUrl(s3) || s3
        || (persisted?.includes('amazonaws.com') ? persisted : '');
    }
  }
  return next;
}

/** Rewrite timeline clip URLs to S3 before cloud PATCH (never persist file://). */
export function sanitizeEditorSnapshotForStorage(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') return snapshot;
  const mapTracks = (tracks) =>
    (tracks || [[]]).map((track) =>
      (Array.isArray(track) ? track.map(sanitizeEditorClipForStorage) : track));
  const mapHostOverlayTracks = (tracks) =>
    (tracks || [[]]).map((track) =>
      (Array.isArray(track) ? track.map(sanitizeHostOverlayClipForStorage) : track));

  const sanitizeBookendSlot = (slot) => {
    if (!slot || typeof slot !== 'object') return slot;
    const next = { ...slot };
    const raw = String(next.url || '').trim();
    const s3 = String(next.url_s3_url || next.urlS3Url || '').trim();
    if (!raw && !s3) return next;
    const { primary, s3Url } = storagePrimaryFromMediaRef(raw, s3, null);
    if (primary && isWebCloudUrl(primary)) {
      next.url = primary;
      if (s3Url) next.url_s3_url = s3Url;
      else delete next.url_s3_url;
    } else if (raw.startsWith('blob:')) {
      next.url = '';
      delete next.url_s3_url;
    } else {
      const persisted = persistedMediaUrl(raw || s3);
      next.url = persisted || raw || s3;
      if (s3 || (persisted && persisted.includes('amazonaws.com'))) {
        next.url_s3_url = persistedMediaUrl(s3) || s3 || (persisted?.includes('amazonaws.com') ? persisted : '');
      }
    }
    return next;
  };

  const bookends = snapshot.videoBookends;
  const videoBookends = bookends && typeof bookends === 'object'
    ? {
      intro: sanitizeBookendSlot(bookends.intro),
      outro: sanitizeBookendSlot(bookends.outro),
    }
    : bookends;

  return {
    ...snapshot,
    videoTracks: mapTracks(snapshot.videoTracks),
    audioTracks: mapTracks(snapshot.audioTracks),
    musicTracks: mapTracks(snapshot.musicTracks),
    subtitleTracks: mapTracks(snapshot.subtitleTracks),
    effectTracks: mapTracks(snapshot.effectTracks),
    overlayTracks: mapHostOverlayTracks(snapshot.overlayTracks),
    ...(videoBookends ? { videoBookends } : {}),
  };
}

/**
 * Normalize persisted editor lanes: top-level null, empty, or null lane entries → `[[], ...]`.
 * Prevents runtime `null[0]` when snapshots omit tracks or store `[null]`.
 */
export function coerceTimelineTracks(tracks) {
  if (!Array.isArray(tracks)) return [[]];
  const lanes = tracks.map((lane) =>
    (Array.isArray(lane) ? lane.filter((c) => c && typeof c === 'object') : []),
  );
  return lanes.length ? lanes : [[]];
}

/** Apply playback URL resolution to timeline clips before rendering / export payloads. */
/** Active clip URL wins over a leftover *_s3_url from an older generation. */
function playbackUrlPreferPrimary(primary, s3Url, localUrl) {
  if (primary) {
    const fromPrimary = mediaUrlForPlayback(primary);
    if (fromPrimary) return fromPrimary;
  }
  return mediaUrlForPlayback(primary, { s3Url, localUrl }) || '';
}

export function normalizeEditorTracksForPlayback(videoTracks, audioTracks, musicTracks) {
  const mapVideo = (c) => ({
    ...c,
    ...(c.videoUrl ? {
      videoUrl: playbackUrlPreferPrimary(c.videoUrl, c.video_s3_url, c.video_local_url),
    } : {}),
    ...(c.imageUrl ? {
      imageUrl: playbackUrlPreferPrimary(c.imageUrl, c.image_s3_url, c.image_local_url),
    } : {}),
    ...(c.audioUrl ? {
      audioUrl: playbackUrlPreferPrimary(c.audioUrl, c.audio_s3_url, c.audio_local_url),
    } : {}),
  });
  const mapAudio = (ac) => ({
    ...ac,
    ...(ac.url ? {
      url: mediaUrlForPlayback(ac.url, {
        s3Url: ac.audio_s3_url || ac.audioS3Url,
        localUrl: ac.audio_local_url || ac.audioLocalUrl,
      }),
    } : {}),
  });
  const mapMusic = (mc) => ({
    ...mc,
    ...(mc.url ? { url: mediaUrlForPlayback(mc.url) } : {}),
  });
  return {
    videoTracks: coerceTimelineTracks(videoTracks).map((t) => t.map(mapVideo)),
    audioTracks: coerceTimelineTracks(audioTracks).map((t) => t.map(mapAudio)),
    musicTracks: coerceTimelineTracks(musicTracks).map((t) => t.map(mapMusic)),
  };
}
